// TODO Move CITextLink into own component

/********************************************* ASSETS *********************************************/
const s = require('./input-util-components.scss');

/************************************** THIRD-PARTY MODULES ***************************************/
import React from 'react';
import cn from 'classnames';
import LaddaButton, {EXPAND_LEFT, XL} from 'react-ladda';
import {withRouter} from 'react-router-dom';
import {omit, RouterProps, RRHistory} from 'mad-utils/lib/react';

/**************************************** PROJECT MODULES *****************************************/
import {onSubmitKeyPress, translate} from '@canimmunize/core-imports';
import {CIFormInput, CIFormInputProps} from './input-components/ci-form-input/ci-form-input';

/*************************************** TYPE DEFINITIONS *****************************************/
interface CITextLinkProps extends RouterProps, NonInjectedCITextLinkProps, ChildrenProps {
    role?: string;
    ariaLabel?: string;
    tabIndex?: number;
    key?: string;
}

interface CIFormInputTitledProps extends CIFormInputProps, CIFormElementTitledProps {}

interface NonInjectedCITextLinkProps {
    onClick?: Function | any;
    mergeClasses?: string;
    id?: string;
}

/******************************************** HELPERS *********************************************/
/**
 * Basic back button click event handler constructor
 *
 * @return {Function} button click handler that acts like pressing back button
 */
const goBack = (history: RRHistory, location, match) => ev => {
    history.goBack();
};

/************************************ CSS CLASS LIST FACTORIES ************************************/
/**
 * TODO Switch this to use styles (below is based on a library requirement)
 *
 * Standard styles object property common across all picklists (or at least
 * typeahead autocomplete picklists)
 *
 * Example: style={getAutocompletePicklistMenuStyles()}
 */
export const getAutocompletePicklistMenuStyles = () => {
    return {
        width: 330,
        maxHeight: `37%`,
        borderRadius: `3px`,
        boxShadow: `0 2px 12px rgba(0, 0, 0, 0.1)`,
        background: `rgba(255, 255, 255, 0.9)`,
        padding: 6,
        fontSize: `90%`,
        position: `fixed`,
        overflow: `auto`,
        zIndex: 100,
        borderWidth: 1,
        borderColor: `black`,
        borderStyle: `solid`,
    };
};

/*************************************************************************************************/
interface CIFormElementTitledProps {
    ciTitle: string;
    ciTitleCssClasses?: string;
    ciSpaceBetweenTitleAndInput?: number | string;
}

/**
 * Wrap input component in title (Title appears above component)
 *
 * @param {Object} props All props accepted by CIFormInput, plus:
 *         |-> @param {string} ciTitle                     Title to display
 *         |-> @param {string} ciTitleCssClasses           Classes to decorate title with    {OPT}
 *         \-> @param {number} ciSpaceBetweenTitleAndInput px between title & input (def: 0) {OPT}
 *
 * @param {Object} children Input form element itself
 */
export const CIFormElementTitled = (props: CIFormElementTitledProps & ChildrenReqProps) => (
    <div>
        <span className={cn(props.ciTitleCssClasses)}>{props.ciTitle}</span>
        <div className={cn(`mb-${props.ciSpaceBetweenTitleAndInput}`)} />
        {React.Children.only(props.children)
            ? React.cloneElement(React.Children.only(props.children), {
                  'aria-label': props.ciTitle,
              })
            : props.children}
    </div>
);

CIFormElementTitled.displayName = `CIFormComponentTitled`;

/*************************************************************************************************/
/**
 * Form input box with title added
 * @param {Object} props All props from CIFormElementTitled and CIFormInput
 */
export const CIFormInputTitled = (props: CIFormInputTitledProps) => (
    <CIFormElementTitled {...props}>
        <CIFormInput
            mergeClasses={cn(props.mergeClasses)}
            {...omit(props, [`ciTitle`, `ciTitleCssClasses`, `ciSpaceBetweenTitleAndInput`])}
        />
    </CIFormElementTitled>
);

CIFormInputTitled.displayName = `CIFormInputTitled`;

/**
 * Convenience function to create an email input box
 */
export const EmailInput = (props: CIFormInputProps) =>
    React.createElement(CIFormInput, Object.assign({type: `email`}, props));

EmailInput.displayName = `EmailInput`;

/**
 * Convenience function to create a password input box
 */
export const PasswordInput = (props: CIFormInputProps) =>
    React.createElement(CIFormInput, Object.assign({type: `password`}, props));

PasswordInput.displayName = `PasswordInput`;

/******************************* SUBMIT / BUTTON / LINK COMPONENTS ********************************/
/**
 * Create text-based button / link
 * Defaults to acting as a back button
 */
export const CITextLink = withRouter((props: CITextLinkProps) => {
    const {history, location, match, onClick, children, ariaLabel, key, id} = props;

    const backStr = translate(`back`).capitalize;
    const cleanAriaLabel = ariaLabel || (typeof children === `string` && children) || backStr;
    const linkKey = key || (typeof children === `string` ? children : ariaLabel) || null;

    return (
        <div
            role={props.role || `button`}
            id={id || `ci-textlink-${cleanAriaLabel}`}
            key={linkKey}
            aria-label={cleanAriaLabel}
            onClick={onClick || goBack(history, location, match)}
            onKeyUp={onSubmitKeyPress(onClick || goBack(history, location, match))}
            tabIndex={typeof props.tabIndex === `number` ? props.tabIndex : 0}
            className={cn(`pointer`, props.mergeClasses)}
        >
            {children || backStr}
        </div>
    );
}) as (props: NonInjectedCITextLinkProps) => JSX.Element;

CITextLink.displayName = `CITextLink`;

/***************************************** BARREL EXPORTS *****************************************/
export {RadioButtonGroup} from './input-components/radio-button-group';
export * from './input-components/autocomplete';
export * from './input-components/select-button';
export * from './input-components/switch-component';
export * from './input-components/ci-spacer';
export * from './input-components/ci-date-picker';
export * from './input-components/hotkey';
export * from './input-components/ci-form-input/ci-form-input';
